/**
 * TemperatureRistorazione — pagina Temperature per variant "ristorazione".
 *
 * Iterazioni:
 *  - R52.4 layout mobile-first wizard
 *  - R52.20 fix input meno (abbattimento/freezer) + struttura analytics
 *    allineata a PulizieRistorazione (tab Settimana | Mese | Punti, diario,
 *    modal dettaglio giorno).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { safePost } from "@/lib/safePost";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { apiErrMsg } from "@/lib/apiErr";
import { fetchTurni, findCurrentTurnoKey, DEFAULT_TURNI_V2 } from "@/lib/turni";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Thermometer, Search, Download, ArrowRight, Camera, X,
  ChevronDown, ChevronUp, Loader2, LayoutGrid, BarChart3, Layers, Pencil, Settings2,
} from "lucide-react";
import { QuickEditDialog } from "@/components/QuickEditDialog";
import RegistroDetailDialog from "@/components/RegistroDetailDialog";

const STATI_DISP = [
  { v: "operativo",     l: "Operativo",            color: "ok" },
  { v: "spento",        l: "Spento",               color: "na" },
  { v: "fuori_uso",     l: "Fuori uso",            color: "na" },
  { v: "manutenzione",  l: "In manutenzione",      color: "na" },
  { v: "ferie",         l: "Locale chiuso (ferie)", color: "na" },
  { v: "altro",         l: "Altro",                color: "na" },
];

const PERIODI = [
  { v: "oggi",      l: "Oggi" },
  { v: "settimana", l: "Settimana" },
  { v: "mese",      l: "Mese" },
  { v: "tutto",     l: "Tutto" },
];

function startOfPeriod(periodo) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (periodo === "settimana") d.setDate(d.getDate() - 7);
  else if (periodo === "mese") d.setDate(d.getDate() - 30);
  else if (periodo === "tutto") return null;
  return d.toISOString();
}

// ── R52.20: helper calendario (allineati a PulizieRistorazione) ───────────
const DAY_LABELS = ["Lu", "Ma", "Me", "Gi", "Ve", "Sa", "Do"];
const MONTHS_IT = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

function dayOfWeekItIdx(d) { return (d.getDay() + 6) % 7; }
// R53.15: ymd in fuso LOCALE (non UTC). Bug pre-fix: in Italia (UTC+1/+2)
// `toISOString().slice(0,10)` su una mezzanotte locale ritorna il giorno PRIMA
// (perché .toISOString converte in UTC). Risultato: ogni filtro "rilevata oggi"
// falliva di un giorno → ogni 4 giorni il calendario settimanale era sfasato.
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
// Converte una stringa ISO UTC (es. "2026-06-27T05:35:00Z") in data locale "YYYY-MM-DD".
function localDateFromISO(isoUtc) {
  if (!isoUtc) return "";
  try {
    const d = new Date(isoUtc);
    if (isNaN(d.getTime())) return String(isoUtc).slice(0, 10);
    return ymd(d);
  } catch { return String(isoUtc).slice(0, 10); }
}
function startOfWeekIt(d = new Date()) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - dayOfWeekItIdx(x));
  return x;
}
function startOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

function colorClassFromPct(pct, hasExpected) {
  if (!hasExpected) return { cell: "bg-slate-200 text-slate-400", dot: "bg-slate-300", text: "text-slate-400" };
  if (pct >= 100) return { cell: "bg-emerald-500 text-white", dot: "bg-emerald-500", text: "text-emerald-600" };
  if (pct >= 50)  return { cell: "bg-amber-500 text-white",   dot: "bg-amber-500",   text: "text-amber-600" };
  return                  { cell: "bg-rose-500 text-white",    dot: "bg-rose-500",    text: "text-rose-600" };
}

function dotClass(rec) {
  if (rec.stato_dispositivo && rec.stato_dispositivo !== "operativo") return "bg-slate-400";
  if (!rec.conforme) {
    if (rec.soglia_max != null && rec.valore != null && rec.valore > rec.soglia_max) return "bg-rose-500";
    if (rec.soglia_min != null && rec.valore != null && rec.valore < rec.soglia_min) return "bg-amber-500";
    return "bg-rose-500";
  }
  return "bg-emerald-500";
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yest = new Date(today); yest.setDate(today.getDate() - 1);
    const dDate = new Date(d); dDate.setHours(0, 0, 0, 0);
    if (dDate.getTime() === today.getTime()) return "Oggi";
    if (dDate.getTime() === yest.getTime()) return "Ieri";
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
  } catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yest = new Date(today); yest.setDate(today.getDate() - 1);
    const dDate = new Date(d); dDate.setHours(0, 0, 0, 0);
    const hm = d.toTimeString().slice(0, 5);
    if (dDate.getTime() === today.getTime()) return `Oggi ${hm}`;
    if (dDate.getTime() === yest.getTime()) return `Ieri ${hm}`;
    return `${d.toLocaleDateString("it-IT", { day: "numeric", month: "short" })} ${hm}`;
  } catch { return iso; }
}

// R52.20: valore di partenza per il wizard — se il template è negativo (freezer/abbattim),
// pre-popoliamo col centro del range così l'operatore non deve scendere da 0 a -18.
function defaultWizardValue(tpl) {
  const mn = tpl?.soglia_min, mx = tpl?.soglia_max;
  if (mn == null && mx == null) return "";
  if (mn != null && mx != null) {
    const center = (Number(mn) + Number(mx)) / 2;
    return Number.isInteger(center) ? String(center) : center.toFixed(1);
  }
  return String(mn ?? mx);
}

// R52.20: parser robusto per il valore digitato — accetta segno meno
// (mobile/desktop), virgola decimale, punto decimale, valori parziali "-" o ",".
function parseTempInput(raw) {
  if (raw == null) return "";
  let s = String(raw).replace(/,/g, ".").trim();
  // Permetti "-" da solo (stato intermedio)
  if (s === "" || s === "-" || s === ".") return s;
  // Mantieni un singolo '-' iniziale, rimuovi altri caratteri non numerici/punto
  const isNeg = s.startsWith("-");
  s = s.replace(/[^\d.]/g, "");
  // Evita doppi punti
  const parts = s.split(".");
  if (parts.length > 2) s = `${parts[0]}.${parts.slice(1).join("")}`;
  return isNeg ? `-${s}` : s;
}

export default function TemperatureRistorazione() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [turniList, setTurniList] = useState(DEFAULT_TURNI_V2);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [periodo, setPeriodo] = useState("oggi");
  const [expandedId, setExpandedId] = useState(null);
  // R52.20: tab vista grafica · "week" | "month" | "punti"
  // R55.0: apertura diretta al tab passato via query param (es. da Impostazioni → "Punti Temperature")
  const [searchParams] = useSearchParams();
  const initialTab = ["week", "month", "punti"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "week";
  const [chartTab, setChartTab] = useState(initialTab);
  // R52.20: modal "Dettaglio giorno"
  const [dayDetail, setDayDetail] = useState(null);

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardQueue, setWizardQueue] = useState([]);
  const [wizardIdx, setWizardIdx] = useState(0);
  const [form, setForm] = useState({});
  const [statoMenuOpen, setStatoMenuOpen] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);

  // R53.13: Quick edit per correggere errori di battitura (endpoint PATCH lato server)
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const saveEdit = async (patched) => {
    try {
      const body = {
        valore: patched.valore === "" || patched.valore == null ? null : parseFloat(String(patched.valore).replace(",", ".")),
        note: patched.note || "",
        correction_note: "Modifica da operatore (correzione errore di battitura)",
      };
      await api.patch(`/temperature/${patched.id}`, body);
      toast.success("Rilevazione corretta · audit log aggiornato");
      await loadAll();
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore modifica"));
      throw e;
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [tpls, recs, turni] = await Promise.all([
        api.get("/temperature-templates").catch(() => ({ data: [] })),
        api.get("/temperature").catch(() => ({ data: [] })),
        fetchTurni(api),
      ]);
      const tplData = tpls.data || [];
      // R52.3: solo storage (frigo/freezer/ambiente/altro) — abbattimento/cottura si
      // gestiscono in Preparazioni
      const filtered = tplData.filter(t =>
        ["frigo", "freezer", "ambiente", "altro"].includes((t.tipo || "").toLowerCase())
      );
      setTemplates(filtered);
      setItems(recs.data || []);
      setTurniList(Array.isArray(turni) && turni.length > 0 ? turni : DEFAULT_TURNI_V2);
    } catch {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  // Punti da rilevare oggi
  // R53.6: match tollerante per id, slug normalizzato e punto.
  // R53.15: TIMEZONE FIX — usa data locale (non UTC) per todayISO.
  // R53.15: rimossa l'esclusione persistente del fix R53.14 (era troppo aggressiva:
  // se un punto aveva mai avuto una rilevazione "spento" in passato, restava
  // nascosto per sempre anche dopo che era tornato operativo). Ora un punto
  // esce dal todo solo se è stato rilevato OGGI (qualsiasi stato).
  // Lo stato "spento" persiste comunque ogni giorno: l'operatore può confermarlo
  // con 1 click nel wizard (precaricato a "spento" se l'ultima rilevazione di
  // ieri era non-operativa) — vedi initWizardForm più sotto.
  const todoToday = useMemo(() => {
    const today = new Date();
    const todayISO = ymd(today);
    const norm = (s) => (s || "").toString().trim().toLowerCase().replace(/\s+/g, " ");
    // R59 — records raggruppati per template (match per id E per nome punto)
    const recsByKey = {};
    items.forEach(r => {
      const keys = [r.template_id, r.punto ? `name:${norm(r.punto)}` : null].filter(Boolean);
      keys.forEach(k => { (recsByKey[k] = recsByKey[k] || []).push(r); });
    });
    const recsFor = (t) => {
      const seen = new Set();
      const out = [];
      [recsByKey[t.id], recsByKey[`name:${norm(t.punto || "")}`]].forEach(list => (list || []).forEach(r => {
        const rid = r.id || r.rilevata_il;
        if (!seen.has(rid)) { seen.add(rid); out.push(r); }
      }));
      return out;
    };
    const currentKey = findCurrentTurnoKey(turniList);
    const currentTurno = turniList.find(x => x.key === currentKey) || turniList[0];
    return templates.filter(t => {
      const recs = recsFor(t);
      const todayRecs = recs.filter(r => localDateFromISO(r.rilevata_il) === todayISO);
      // R59 — 2x/giorno: da rilevare se manca la lettura nel TURNO CORRENTE
      // (coerente con badge Home che conta solo lo slot corrente).
      if (t.due_volte_giorno && currentTurno) {
        return !todayRecs.some(r => {
          const h = new Date(r.rilevata_il).getHours();
          return h >= currentTurno.start_h && h < currentTurno.end_h;
        });
      }
      if (todayRecs.length > 0) return false;
      // R59 — frequenza multi-giorno (es. ogni 3 gg): da rilevare SOLO se
      // l'ultima lettura è fuori dalla finestra di frequenza (coerente con
      // /tasks/today che usa frequenza_giorni per il badge Home).
      const freq = Math.max(parseInt(t.frequenza_giorni, 10) || 1, 1);
      if (freq > 1) {
        let lastDate = "";
        recs.forEach(r => { const d = localDateFromISO(r.rilevata_il); if (d > lastDate) lastDate = d; });
        if (lastDate) {
          const days = Math.round((new Date(todayISO) - new Date(lastDate)) / 86400000);
          return days >= freq;
        }
      }
      return true;
    });
  }, [templates, items, turniList]);

  // R53.15: "Punti fermi" = punti la cui ULTIMA rilevazione è di OGGI ed è in
  // stato non-operativo. Visualizzazione informativa per dare contesto quando
  // il sistema dice "tutto fatto" (es. "Ok, 0 da rilevare, ma 1 è spento").
  const fermiList = useMemo(() => {
    const norm = (s) => (s || "").toString().trim().toLowerCase().replace(/\s+/g, " ");
    const NON_OPERATIVI = new Set(["spento", "fuori_uso", "manutenzione", "ferie"]);
    const todayISO = ymd(new Date());
    const lastByKey = {};
    items.forEach(r => {
      const keys = [r.template_id, r.punto ? `name:${norm(r.punto)}` : null].filter(Boolean);
      keys.forEach(k => {
        const cur = lastByKey[k];
        if (!cur || (r.rilevata_il || "") > (cur.rilevata_il || "")) lastByKey[k] = r;
      });
    });
    return templates.map(t => {
      const lastRec = lastByKey[t.id] || lastByKey[`name:${norm(t.punto || "")}`];
      if (!lastRec) return null;
      const stato = (lastRec.stato_dispositivo || "").toLowerCase();
      if (!NON_OPERATIVI.has(stato)) return null;
      const lastDate = localDateFromISO(lastRec.rilevata_il);
      if (lastDate !== todayISO) return null; // mostriamo solo i fermi di OGGI
      return { template: t, stato, since: lastDate, rec: lastRec };
    }).filter(Boolean);
  }, [templates, items]);

  // Storico filtrato (search + periodo)
  const filteredItems = useMemo(() => {
    const startISO = startOfPeriod(periodo);
    const q = (search || "").toLowerCase().trim();
    return items.filter(r => {
      if (startISO && (r.rilevata_il || "") < startISO) return false;
      if (!q) return true;
      const hay = `${r.punto || ""} ${r.note || ""}`.toLowerCase();
      return hay.includes(q);
    }).sort((a, b) => (b.rilevata_il || "").localeCompare(a.rilevata_il || ""));
  }, [items, periodo, search]);

  // ── R52.20: STATS per le 3 viste grafiche ─────────────────────────────
  // R53.15: restDays per evitare di marcare rosso i giorni di chiusura settimanale
  const restDays = useMemo(() => new Set((user?.giorni_riposo || []).filter(d => Number.isInteger(d) && d >= 0 && d <= 6)), [user?.giorni_riposo]);

  const stats = useMemo(() => {
    const itemsByDay = {};
    items.forEach(r => {
      // R53.15: usa data LOCALE (non UTC) — vedi commento ymd
      const d = localDateFromISO(r.rilevata_il);
      if (!d) return;
      (itemsByDay[d] = itemsByDay[d] || []).push(r);
    });

    // attesi/giorno: ogni template ha frequenza_giorni → contribuisce 1/freq per giorno
    const expectedPerDay = templates.reduce((acc, t) => {
      const f = Math.max(1, Number(t.frequenza_giorni || 1));
      return acc + (1 / f);
    }, 0);
    const expectedRound = Math.round(expectedPerDay);

    const buildDayCell = (date) => {
      const iso = ymd(date);
      const all = itemsByDay[iso] || [];
      // "successo" = rilevazione conforme oppure stato non operativo VALIDO (ferie/spento)
      const okRecords = all.filter(r => {
        if (r.stato_dispositivo && r.stato_dispositivo !== "operativo") {
          return ["spento", "ferie", "manutenzione", "fuori_uso"].includes(r.stato_dispositivo);
        }
        return r.conforme === true;
      });
      const done = all.length;
      const ok = okRecords.length;
      // R53.15: giorno di CHIUSURA settimanale dell'org → cella "rest" (slate)
      // anche se ci sono 0 rilevazioni; sennò il calendario marca rosso un giorno
      // in cui nessuno doveva lavorare.
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const dNorm = new Date(date); dNorm.setHours(0, 0, 0, 0);
      const isToday = dNorm.getTime() === today.getTime();
      const isFuture = dNorm.getTime() > today.getTime();
      const dowIt = (date.getDay() + 6) % 7; // 0=Lun..6=Dom
      const isRest = restDays.has(dowIt);
      // R53.14/R53.15: cap expected al massimo tra atteso e ok reale
      const expectedFinal = Math.max(expectedRound, ok);
      const pct = expectedFinal > 0 ? (ok / expectedFinal) * 100 : 0;
      return { iso, date, done, ok, expected: expectedFinal, pct, isToday, isFuture, isRest, items: all };
    };

    // WEEK
    const wStart = startOfWeekIt();
    const week = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(wStart); d.setDate(wStart.getDate() + i);
      week.push(buildDayCell(d));
    }

    // MONTH
    const mStart = startOfMonth();
    const mEnd = endOfMonth();
    const padBefore = dayOfWeekItIdx(mStart);
    const days = [];
    for (let i = 0; i < padBefore; i++) days.push(null);
    for (let d = new Date(mStart); d <= mEnd; d.setDate(d.getDate() + 1)) {
      days.push(buildDayCell(new Date(d)));
    }
    while (days.length % 7 !== 0) days.push(null);

    // PUNTI · score per punto temperatura (ultimi 30 gg)
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30); cutoff.setHours(0, 0, 0, 0);
    const cutoffIso = ymd(cutoff);
    const byPunto = {};
    templates.forEach(t => {
      const f = Math.max(1, Number(t.frequenza_giorni || 1));
      const exp = 30 / f;
      byPunto[t.punto] = { punto: t.punto, tipo: t.tipo, expected: exp, ok: 0, nc: 0 };
    });
    items.forEach(r => {
      const d = (r.rilevata_il || "").slice(0, 10);
      if (!d || d < cutoffIso) return;
      const key = r.punto;
      if (!byPunto[key]) byPunto[key] = { punto: key, tipo: r.tipo, expected: 0, ok: 0, nc: 0 };
      const validNonOp = r.stato_dispositivo && r.stato_dispositivo !== "operativo"
        && ["spento", "ferie", "manutenzione"].includes(r.stato_dispositivo);
      if (validNonOp || r.conforme === true) byPunto[key].ok += 1;
      else if (r.conforme === false) byPunto[key].nc += 1;
    });
    const punti = Object.values(byPunto).map(p => {
      // R53.14: cap expected = max(atteso, ok) per evitare cella ambra/rossa
      // anche se hai rilevato tutto e di più.
      const expectedFinal = Math.max(Math.round(p.expected), p.ok);
      return {
        ...p,
        expected: expectedFinal,
        pct: expectedFinal > 0 ? Math.min(100, Math.round((p.ok / expectedFinal) * 100)) : 0,
      };
    }).sort((a, b) => a.pct - b.pct);

    return { week, monthDays: days, punti };
  }, [items, templates, restDays]);

  // Diario: raggruppa items per giorno (decrescente)
  const diary = useMemo(() => {
    const byDay = {};
    filteredItems.forEach(r => {
      const d = (r.rilevata_il || "").slice(0, 10);
      if (!d) return;
      (byDay[d] = byDay[d] || []).push(r);
    });
    return Object.entries(byDay)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([dateIso, list]) => {
        const ok = list.filter(r => {
          if (r.stato_dispositivo && r.stato_dispositivo !== "operativo") {
            return ["spento", "ferie", "manutenzione"].includes(r.stato_dispositivo);
          }
          return r.conforme === true;
        }).length;
        return { dateIso, items: list, ok, total: list.length };
      });
  }, [filteredItems]);

  // ── Wizard ────────────────────────────────────────────────────────────
  const startWizard = () => {
    if (todoToday.length === 0) { toast.info("Tutti i punti sono già stati rilevati oggi"); return; }
    setWizardQueue(todoToday);
    setWizardIdx(0);
    initWizardForm(todoToday[0]);
    setWizardOpen(true);
  };

  const initWizardForm = (tpl) => {
    // R53.15: precarico stato_dispositivo se l'ultima rilevazione di questo punto
    // era non-operativa. Così l'operatore preme "Salva" e conferma con 1 click.
    const norm = (s) => (s || "").toString().trim().toLowerCase().replace(/\s+/g, " ");
    let lastStato = "operativo";
    let lastRec = null;
    for (const r of items) {
      const matchTpl = tpl.id && r.template_id === tpl.id;
      const matchName = !matchTpl && tpl.punto && norm(r.punto) === norm(tpl.punto);
      if (!matchTpl && !matchName) continue;
      if (!lastRec || (r.rilevata_il || "") > (lastRec.rilevata_il || "")) lastRec = r;
    }
    if (lastRec) {
      const s = (lastRec.stato_dispositivo || "").toLowerCase();
      if (["spento", "ferie", "manutenzione", "fuori_uso"].includes(s)) lastStato = s;
    }
    setForm({
      template_id: tpl.id,
      punto: tpl.punto,
      tipo: tpl.tipo || "frigo",
      tipo_controllo: tpl.tipo_controllo || "manuale",
      soglia_min: tpl.soglia_min ?? null,
      soglia_max: tpl.soglia_max ?? null,
      // R52.20: pre-popola col centro del range del template, così l'operatore
      // non deve scendere da 0 a -18 per il freezer/abbattimento.
      valore: defaultWizardValue(tpl),
      note: "",
      stato_dispositivo: lastStato,
      photo_path: null,
      photo_filename: null,
      photo_content_type: null,
    });
    setStatoMenuOpen(false);
  };

  const adjustValue = (delta) => {
    const cur = parseFloat(String(form.valore || "").replace(",", "."));
    const next = (isNaN(cur) ? 0 : cur) + delta;
    setForm({ ...form, valore: next.toFixed(1) });
  };

  const fileRef = useRef(null);
  const onPhotoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSavingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: up } = await api.post("/temperature/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      let aiValue = null;
      try {
        const fd2 = new FormData();
        fd2.append("file", f);
        const { data: ex } = await api.post("/temperature/extract-from-photo", fd2, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (ex?.temperature != null) aiValue = ex.temperature;
      } catch { /* AI best-effort */ }
      setForm(prev => ({
        ...prev,
        photo_path: up.storage_path,
        photo_filename: up.original_filename || f.name,
        photo_content_type: up.content_type || f.type,
        valore: (aiValue != null && (prev.stato_dispositivo || "operativo") === "operativo")
          ? String(aiValue) : prev.valore,
      }));
      toast.success(aiValue != null ? `Valore letto dall'AI: ${aiValue}°C` : "Foto caricata");
    } catch {
      toast.error("Errore upload foto");
    } finally {
      setSavingPhoto(false);
    }
  };

  const saveCurrent = async () => {
    const stato = form.stato_dispositivo || "operativo";
    const isOp = stato === "operativo";
    // R52.20: normalizza il valore digitato prima di validare/inviare
    const raw = String(form.valore ?? "").replace(",", ".").trim();
    if (isOp && (raw === "" || raw === "-" || raw === "." || isNaN(parseFloat(raw)))) {
      toast.error("Inserisci un valore valido (può essere negativo, es. -18.5)");
      return;
    }
    if (stato === "altro" && !(form.note || "").trim()) {
      toast.error("Per stato 'Altro' la nota è obbligatoria");
      return;
    }
    try {
      const res = await safePost("/temperature", {
        punto: form.punto,
        tipo: form.tipo,
        tipo_controllo: form.tipo_controllo,
        valore: isOp ? parseFloat(raw) : null,
        soglia_min: form.soglia_min,
        soglia_max: form.soglia_max,
        note: form.note || "",
        stato_dispositivo: stato,
        photo_path: form.photo_path,
        photo_filename: form.photo_filename,
        photo_content_type: form.photo_content_type,
      }, { label: `Temperatura · ${form.punto || form.tipo || ""}` });
      if (res?.offline) {
        toast.success("Salvata offline · si sincronizzerà appena torna la rete");
      } else {
        toast.success("Rilevazione salvata");
      }
      goNext();
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore salvataggio"));
    }
  };

  const goNext = () => {
    const next = wizardIdx + 1;
    if (next < wizardQueue.length) {
      setWizardIdx(next);
      initWizardForm(wizardQueue[next]);
    } else {
      setWizardOpen(false);
      loadAll();
    }
  };

  // ── CSV export ────────────────────────────────────────────────────────
  const exportCsv = () => {
    const headers = ["Data", "Punto", "Tipo", "Valore", "Soglia Min", "Soglia Max", "Stato", "Conforme", "Nota"];
    const rows = filteredItems.map(r => [
      r.rilevata_il || "",
      r.punto || "",
      r.tipo || "",
      r.valore != null ? r.valore : "",
      r.soglia_min != null ? r.soglia_min : "",
      r.soglia_max != null ? r.soglia_max : "",
      r.stato_dispositivo || "operativo",
      r.conforme ? "Sì" : "No",
      (r.note || "").replace(/[\r\n]/g, " "),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `temperature_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const currentTpl = wizardQueue[wizardIdx];
  const wizardConforme = (() => {
    if (!currentTpl) return null;
    const raw = String(form.valore ?? "").replace(",", ".").trim();
    if (raw === "" || raw === "-" || raw === ".") return null;
    const v = parseFloat(raw);
    if (isNaN(v)) return null;
    if (form.stato_dispositivo && form.stato_dispositivo !== "operativo") return null;
    if (currentTpl.soglia_min != null && v < Number(currentTpl.soglia_min)) return "low";
    if (currentTpl.soglia_max != null && v > Number(currentTpl.soglia_max)) return "high";
    return "ok";
  })();

  return (
    <div className="min-h-screen bg-slate-100 pb-24" data-testid="temperature-ristorazione">
      <header className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-5 pt-5 pb-4 rounded-b-2xl shadow-lg">
        <div className="max-w-md lg:max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-md shrink-0">
            <Thermometer className="w-6 h-6 text-[#13294B]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">e-Log Ristorazione</p>
            <h1 className="text-xl font-bold leading-tight">Temperature</h1>
          </div>
          {user?.role === "admin" && (
            <Link
              to="/setup-temperature"
              className="w-10 h-10 rounded-full hover:bg-white/15 flex items-center justify-center transition"
              aria-label="Impostazioni punti temperature"
              title="Impostazioni punti temperature"
              data-testid="temp-settings-shortcut"
            >
              <Settings2 className="w-5 h-5 text-white/90" />
            </Link>
          )}
        </div>
      </header>

      {/* CARD DA RILEVARE OGGI */}
      <section className="max-w-md lg:max-w-3xl mx-auto px-4 -mt-3">
        <div className="bg-white rounded-2xl shadow-md p-4 border border-slate-200" data-testid="todo-card">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <Thermometer className="w-6 h-6 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Da rilevare oggi</p>
              <p className="text-2xl font-extrabold text-slate-900 leading-tight" data-testid="todo-count">
                {todoToday.length} {todoToday.length === 1 ? "punto" : "punti"}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                {todoToday.length === 0 ? "Tutti i punti rilevati oggi ✓" : todoToday.slice(0, 3).map(t => t.punto).join(" · ")}
              </p>
            </div>
          </div>
          <button
            onClick={startWizard}
            disabled={todoToday.length === 0}
            className="mt-3 w-full bg-[#13294B] text-white rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 active:scale-[.98] disabled:opacity-50"
            data-testid="start-wizard-btn"
          >
            <ArrowRight className="w-4 h-4" />
            {todoToday.length === 0 ? "Tutto fatto per oggi" : "Inizia rilevazione"}
          </button>
        </div>
      </section>

      {/* R53.15: Punti fermi OGGI (registrati come spenti/manutenzione/ferie nelle
          rilevazioni di oggi) — info contestuale per dare risposta visiva all'operatore. */}
      {fermiList.length > 0 && (
        <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-3" data-testid="fermi-section">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
              Punti registrati come fermi oggi ({fermiList.length})
            </p>
            <ul className="space-y-1.5">
              {fermiList.map(({ template, stato, since }) => {
                const statoLabel = STATI_DISP.find(s => s.v === stato)?.l || stato;
                return (
                  <li key={template.id || template.punto} className="flex items-center gap-2 text-[12px]" data-testid={`fermo-${template.id || template.punto}`}>
                    <span className="w-2 h-2 rounded-full bg-slate-400 shrink-0" />
                    <span className="text-slate-700 truncate flex-1">{template.punto}</span>
                    <span className="text-[10px] text-slate-500 shrink-0">{statoLabel}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">dal {since || "—"}</span>
                  </li>
                );
              })}
            </ul>
            <p className="text-[10px] text-slate-400 mt-2 italic">
              Domani il punto tornerà in lista: confermerai lo stato con 1 click.
            </p>
          </div>
        </section>
      )}

      {/* R52.20: TAB CAROSELLO viste grafiche (stesso pattern Pulizie) */}
      <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-5" data-testid="chart-tabs">
        <div className="bg-white rounded-xl p-1 border border-slate-200 flex gap-1">
          {[
            { v: "week",  l: "Settimana", I: LayoutGrid },
            { v: "month", l: "Mese",      I: BarChart3 },
            { v: "punti", l: "Punti",     I: Layers },
          ].map(t => {
            const Ico = t.I;
            return (
              <button
                key={t.v}
                onClick={() => setChartTab(t.v)}
                className={`flex-1 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 ${
                  chartTab === t.v ? "bg-[#13294B] text-white" : "text-slate-600"
                }`}
                data-testid={`tab-${t.v}`}
              ><Ico className="w-3.5 h-3.5" />{t.l}</button>
            );
          })}
        </div>
      </section>

      {/* VISTA SETTIMANA */}
      {chartTab === "week" && (
        <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-3" data-testid="view-week">
          <div className="bg-white rounded-2xl border border-slate-200 p-3 shadow-sm">
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">Questa settimana</p>
            <div className="grid grid-cols-7 gap-1.5">
              {stats.week.map((c, i) => {
                const disabled = c.isFuture || c.isToday;
                const cls = c.isToday
                  ? "bg-blue-100 text-blue-700 ring-2 ring-blue-300"
                  : c.isFuture
                    ? "bg-slate-100 text-slate-400"
                    // R53.15: giorno chiusura → slate "Chiuso"
                    : c.isRest && c.done === 0
                      ? "bg-slate-100 text-slate-500"
                      : colorClassFromPct(c.pct, c.expected > 0).cell;
                return (
                  <button
                    key={i}
                    onClick={() => !disabled && setDayDetail(c)}
                    disabled={disabled}
                    className={`aspect-square rounded-lg flex flex-col items-center justify-center font-bold text-[10px] ${cls} disabled:cursor-default`}
                    data-testid={`week-cell-${i}`}
                  >
                    <span>{DAY_LABELS[i]}</span>
                    <span className="text-[8px] opacity-90">
                      {c.isToday ? "oggi" : c.isFuture ? "—" : c.isRest && c.done === 0 ? "chiuso" : `${c.ok}/${c.expected || "?"}`}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-2 text-center italic">Tocca un giorno per i dettagli</p>
          </div>
        </section>
      )}

      {/* VISTA MESE */}
      {chartTab === "month" && (
        <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-3" data-testid="view-month">
          <div className="bg-white rounded-2xl border border-slate-200 p-3 shadow-sm">
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">
              Heatmap · {MONTHS_IT[new Date().getMonth()]} {new Date().getFullYear()}
            </p>
            <div className="grid grid-cols-7 gap-1 text-center text-[9px] text-slate-400 font-semibold mb-1">
              {DAY_LABELS.map(l => <div key={l}>{l[0]}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {stats.monthDays.map((c, i) => {
                if (!c) return <div key={i} />;
                const disabled = c.isFuture || c.isToday;
                const cls = c.isToday
                  ? "bg-blue-100 text-blue-700 ring-2 ring-blue-300"
                  : c.isFuture
                    ? "bg-slate-100 text-slate-400"
                    // R53.15: giorno chiusura settimanale → slate
                    : c.isRest && c.done === 0
                      ? "bg-slate-100 text-slate-400"
                      : colorClassFromPct(c.pct, c.expected > 0).cell;
                return (
                  <button
                    key={i}
                    onClick={() => !disabled && setDayDetail(c)}
                    disabled={disabled}
                    className={`aspect-square rounded text-[9px] font-semibold flex items-center justify-center ${cls}`}
                  >{c.date.getDate()}</button>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-2 mt-2 text-[9px] text-slate-500">
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500"/>100%</div>
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-amber-500"/>50-99%</div>
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-rose-500"/>&lt;50%</div>
            </div>
          </div>
        </section>
      )}

      {/* VISTA PUNTI */}
      {chartTab === "punti" && (
        <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-3" data-testid="view-punti">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 px-3 pt-3 pb-1">
              Performance per punto · ultimi 30 gg
            </p>
            {stats.punti.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-slate-500">Nessun punto configurato.</div>
            )}
            <div className="divide-y divide-slate-100">
              {stats.punti.map(p => {
                const c = colorClassFromPct(p.pct, p.expected > 0);
                return (
                  <div key={p.punto} className="px-3 py-3 flex items-center gap-3" data-testid={`punto-row-${p.punto}`}>
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                      <Thermometer className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-slate-900 truncate">{p.punto}</p>
                      <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${c.dot}`} style={{ width: `${Math.min(100, p.pct)}%` }} />
                      </div>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {p.ok} ok su {p.expected} attesi{p.nc > 0 && <> · <span className="text-rose-600 font-semibold">{p.nc} NC</span></>}
                      </p>
                    </div>
                    <div className={`text-right ${c.text}`}>
                      <div className="font-extrabold text-xl">{p.pct}%</div>
                      <div className={`w-2.5 h-2.5 rounded-full inline-block ${c.dot}`}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SEARCH + FILTRI */}
      <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-5 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#13294B]"
            placeholder="Cerca per punto, nota…"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          {PERIODI.map(p => (
            <button
              key={p.v}
              onClick={() => setPeriodo(p.v)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition ${
                periodo === p.v ? "bg-[#13294B] text-white" : "bg-white text-slate-600 border border-slate-200"
              }`}
              data-testid={`chip-${p.v}`}
            >{p.l}</button>
          ))}
        </div>
      </section>

      {/* R52.20: DIARIO raggruppato per giorno */}
      <main className="max-w-md lg:max-w-3xl mx-auto px-4 py-4">
        <h2 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 px-1 mb-2">
          Storico diario <span className="normal-case text-slate-400 font-normal">({filteredItems.length})</span>
        </h2>

        {loading && <div className="text-center text-slate-500 text-sm py-8">Caricamento…</div>}
        {!loading && diary.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="empty-state">
            Nessuna rilevazione nel periodo selezionato.
          </div>
        )}

        {!loading && (
          <ul className="space-y-2" data-testid="diary-list">
            {diary.map(day => {
              const expanded = expandedId === day.dateIso;
              const pct = day.total > 0 ? (day.ok / day.total) * 100 : 0;
              const c = colorClassFromPct(pct, day.total > 0);
              return (
                <li key={day.dateIso} className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid={`diary-day-${day.dateIso}`}>
                  <button onClick={() => setExpandedId(expanded ? null : day.dateIso)} className="w-full flex items-center justify-between p-3 text-left">
                    <div>
                      <p className="font-bold text-sm">📅 {fmtDate(day.dateIso)}</p>
                      <p className="text-[11px] text-slate-500">{day.dateIso}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
                      <span className={`font-bold text-sm ${c.text}`}>{day.ok}/{day.total}</span>
                      {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                    </div>
                  </button>
                  {expanded && (
                    <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-1.5">
                      {day.items.map(r => {
                        const isNotOp = r.stato_dispositivo && r.stato_dispositivo !== "operativo";
                        const statoLabel = STATI_DISP.find(s => s.v === r.stato_dispositivo)?.l || "Operativo";
                        const canEdit = user?.role !== "operator";
                        return (
                          <div key={r.id} className="flex items-center gap-2 text-[12px]">
                            <button onClick={() => setDetail(r)} className="flex items-center gap-2 flex-1 min-w-0 text-left active:opacity-70" data-testid={`detail-${r.id}`}>
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotClass(r)}`} />
                              <span className="text-slate-700 truncate flex-1">{r.punto}</span>
                              {r.photo_path && <Camera className="w-3 h-3 text-slate-400 shrink-0" />}
                              <span className="text-slate-400 text-[11px] shrink-0">
                                {(r.rilevata_il || r.created_at || "").slice(11, 16)}
                              </span>
                              <span className={`text-[11px] ml-2 font-semibold shrink-0 ${isNotOp ? "text-slate-500" : r.conforme ? "text-emerald-700" : "text-rose-600"}`}>
                                {isNotOp ? statoLabel : (r.valore != null ? `${r.valore}°C` : "—")}
                              </span>
                            </button>
                            {canEdit && (
                              <button onClick={(e) => { e.stopPropagation(); setEditing(r); }}
                                      className="ml-1 p-1 rounded hover:bg-blue-50 text-blue-700"
                                      data-testid={`edit-${r.id}`} title="Modifica">
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {filteredItems.length > 0 && (
          <button
            onClick={exportCsv}
            className="mt-5 w-full py-3 rounded-2xl border border-slate-300 bg-white text-slate-800 font-semibold text-sm flex items-center justify-center gap-2"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" /> Esporta CSV ({filteredItems.length} rilevazioni)
          </button>
        )}
      </main>

      {/* R52.20: MODAL DETTAGLIO GIORNO */}
      <Dialog open={!!dayDetail} onOpenChange={(o) => { if (!o) setDayDetail(null); }}>
        <DialogContent className="max-w-sm p-0 overflow-hidden gap-0 [&>button.absolute]:hidden" data-testid="day-detail-dialog">
          <DialogTitle className="sr-only">Dettaglio giorno</DialogTitle>
          <DialogDescription className="sr-only">Rilevazioni temperature in questa giornata.</DialogDescription>
          {dayDetail && (
            <>
              <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
                    {dayDetail.date.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                  </p>
                  <h3 className="font-bold text-base">{dayDetail.ok} su {dayDetail.expected || "?"} conformi</h3>
                </div>
                <button onClick={() => setDayDetail(null)} className="shrink-0 w-10 h-10 rounded-full hover:bg-white/15 flex items-center justify-center" aria-label="Chiudi" data-testid="day-detail-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
                {dayDetail.items.length === 0 ? (
                  <p className="text-center text-sm text-slate-500 py-6">Nessuna rilevazione in questo giorno.</p>
                ) : dayDetail.items.map(r => {
                  const isNotOp = r.stato_dispositivo && r.stato_dispositivo !== "operativo";
                  const validNonOp = isNotOp && ["spento", "ferie", "manutenzione"].includes(r.stato_dispositivo);
                  const statoLabel = STATI_DISP.find(s => s.v === r.stato_dispositivo)?.l;
                  const bg = isNotOp
                    ? (validNonOp ? "bg-slate-50" : "bg-rose-50")
                    : (r.conforme ? "bg-emerald-50" : "bg-rose-50");
                  return (
                    <div key={r.id} className={`flex items-center gap-3 p-2 rounded-xl ${bg}`}>
                      <div className={`w-2.5 h-2.5 rounded-full ${dotClass(r)}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{r.punto}</p>
                        <p className="text-[10px] text-slate-500">
                          {(r.rilevata_il || "").slice(11, 16)}
                          {isNotOp && ` · ${statoLabel}`}
                        </p>
                      </div>
                      <span className={`text-sm font-extrabold ${isNotOp ? "text-slate-500" : r.conforme ? "text-emerald-700" : "text-rose-600"}`}>
                        {isNotOp ? "N/A" : (r.valore != null ? `${r.valore}°C` : "—")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ──────────── WIZARD MODAL ──────────── */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent
          className="max-w-sm p-0 overflow-hidden gap-0 [&>button.absolute]:hidden"
          data-testid="wizard-dialog"
        >
          <DialogTitle className="sr-only">Rilevazione · {currentTpl?.punto || ""}</DialogTitle>
          <DialogDescription className="sr-only">
            Inserisci la temperatura rilevata per il punto {currentTpl?.punto || ""}. Puoi anche caricare una foto del termometro.
          </DialogDescription>
          {currentTpl && (
            <>
              {/* Wizard header */}
              <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
                    Punto {wizardIdx + 1} di {wizardQueue.length}
                  </p>
                  <h3 className="font-bold text-base truncate">{currentTpl.punto}</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setWizardOpen(false)}
                  className="shrink-0 w-10 h-10 rounded-full hover:bg-white/15 flex items-center justify-center"
                  aria-label="Chiudi"
                  data-testid="wiz-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-3">
                <div className="bg-slate-100 rounded-xl px-3 py-2 text-[11px] text-slate-600">
                  Range conforme: <b className="text-slate-900">
                    {currentTpl.soglia_min ?? "—"}°C ÷ {currentTpl.soglia_max ?? "—"}°C
                  </b> · Tipo: {currentTpl.tipo}
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700">Valore °C</label>
                  <div className="mt-1 flex gap-2 items-stretch min-w-0">
                    <button
                      type="button"
                      onClick={() => adjustValue(-0.5)}
                      disabled={form.stato_dispositivo !== "operativo"}
                      className="shrink-0 w-11 h-12 rounded-xl bg-slate-100 text-slate-700 font-bold text-lg disabled:opacity-40 active:scale-95 transition"
                      data-testid="wiz-minus"
                      aria-label="Diminuisci di 0.5"
                    >–</button>
                    {/* R52.20: input come text + inputMode decimal per consentire segno meno
                        e virgola decimale sulla tastiera mobile (la "number" non li mostra). */}
                    <input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={form.valore ?? ""}
                      onChange={(e) => setForm({ ...form, valore: parseTempInput(e.target.value) })}
                      disabled={form.stato_dispositivo !== "operativo"}
                      placeholder={form.stato_dispositivo !== "operativo" ? "N/A" : "0.0"}
                      className={`flex-1 min-w-0 h-12 rounded-xl border text-center text-xl font-extrabold disabled:bg-slate-50 disabled:text-slate-400 ${
                        wizardConforme === "ok"   ? "border-emerald-300 text-emerald-700 bg-emerald-50/40" :
                        wizardConforme === "low"  ? "border-amber-300 text-amber-700 bg-amber-50/40" :
                        wizardConforme === "high" ? "border-rose-300 text-rose-700 bg-rose-50/40" :
                        "border-slate-300"
                      }`}
                      data-testid="wiz-valore"
                    />
                    <button
                      type="button"
                      onClick={() => adjustValue(0.5)}
                      disabled={form.stato_dispositivo !== "operativo"}
                      className="shrink-0 w-11 h-12 rounded-xl bg-slate-100 text-slate-700 font-bold text-lg disabled:opacity-40 active:scale-95 transition"
                      data-testid="wiz-plus"
                      aria-label="Aumenta di 0.5"
                    >+</button>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Suggerimento: scrivi direttamente il valore (anche negativo, es. <code>-18</code>) oppure usa i tasti +/−.
                  </p>
                  {wizardConforme === "high" && (
                    <p className="text-[11px] text-rose-700 font-semibold mt-1" data-testid="wiz-warn-high">
                      ⚠ Sopra soglia max ({currentTpl.soglia_max}°C) — verrà creata una NC.
                    </p>
                  )}
                  {wizardConforme === "low" && (
                    <p className="text-[11px] text-amber-700 font-semibold mt-1" data-testid="wiz-warn-low">
                      ⚠ Sotto soglia min ({currentTpl.soglia_min}°C) — verrà creata una NC.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 relative">
                  <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhotoChange} className="hidden" />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={savingPhoto}
                    className="h-11 rounded-xl border border-slate-300 bg-white text-slate-700 text-sm font-semibold flex items-center justify-center gap-1.5 active:scale-[.99] disabled:opacity-50"
                    data-testid="wiz-photo"
                  >
                    {savingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                    {form.photo_path ? "Foto caricata ✓" : "📷 Foto"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatoMenuOpen(o => !o)}
                    className="h-11 rounded-xl border border-slate-300 bg-white text-slate-700 text-sm font-semibold flex items-center justify-center gap-1.5"
                    data-testid="wiz-stato-btn"
                  >
                    {STATI_DISP.find(s => s.v === form.stato_dispositivo)?.l || "Operativo"}
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  {statoMenuOpen && (
                    <div className="absolute right-0 top-[3.2rem] z-10 bg-white border border-slate-200 rounded-xl shadow-lg w-44 py-1 text-sm">
                      {STATI_DISP.map(s => (
                        <button
                          key={s.v}
                          type="button"
                          onClick={() => { setForm({ ...form, stato_dispositivo: s.v }); setStatoMenuOpen(false); }}
                          className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${form.stato_dispositivo === s.v ? "font-semibold text-[#13294B]" : ""}`}
                          data-testid={`wiz-stato-${s.v}`}
                        >{s.l}</button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700">
                    Nota {form.stato_dispositivo === "altro" && <span className="text-rose-600">*</span>}
                  </label>
                  <input
                    type="text"
                    value={form.note || ""}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder={form.stato_dispositivo === "altro" ? "Obbligatoria per 'Altro'" : "Opzionale"}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm"
                    data-testid="wiz-note"
                  />
                </div>
              </div>

              {/* Wizard footer */}
              <div className="border-t border-slate-200 px-4 py-3 flex gap-2">
                <button
                  type="button"
                  onClick={goNext}
                  className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-700 font-semibold"
                  data-testid="wiz-skip"
                >Salta</button>
                <button
                  type="button"
                  onClick={saveCurrent}
                  className="flex-1 h-12 rounded-xl bg-[#13294B] text-white font-bold flex items-center justify-center gap-1.5 active:scale-95 transition"
                  data-testid="wiz-save"
                >
                  Salva
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* R53.13: Quick edit (PATCH) — correzione errore battitura */}
      <QuickEditDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        record={editing}
        title={`Correggi rilevazione · ${editing?.punto || ""}`}
        fields={[
          { key: "valore", label: "Valore °C", type: "number-signed", helper: "Usa ± per inserire valori negativi (es. freezer −18°C)" },
          { key: "note", label: "Note", type: "textarea" },
        ]}
        onSave={saveEdit}
        onDelete={["admin", "superadmin"].includes(user?.role) ? async (rec) => {
          try {
            await api.delete(`/temperature/${rec.id}`);
            toast.success("Registrazione eliminata");
            await loadAll();
          } catch (e) { toast.error(apiErrMsg(e, "Errore eliminazione")); throw e; }
        } : undefined}
      />

      {/* Scheda dettaglio rilevazione (read-only, con foto) */}
      <RegistroDetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        statusLabel={detail ? (detail.stato_dispositivo && detail.stato_dispositivo !== "operativo"
          ? (STATI_DISP.find(s => s.v === detail.stato_dispositivo)?.l || "Non operativo")
          : (detail.conforme ? "Conforme" : "Fuori soglia")) : ""}
        statusTone={detail ? (detail.stato_dispositivo && detail.stato_dispositivo !== "operativo" ? "neutral" : (detail.conforme ? "ok" : "bad")) : "neutral"}
        title={detail?.punto}
        subtitle={detail ? `${(detail.rilevata_il || detail.created_at || "").slice(0, 10)} · ore ${(detail.rilevata_il || detail.created_at || "").slice(11, 16)}` : ""}
        cells={detail ? [
          { label: "Valore", value: detail.stato_dispositivo && detail.stato_dispositivo !== "operativo" ? "N/A" : (detail.valore != null ? `${detail.valore}°C` : "—"), tone: detail.conforme ? undefined : "bad", mono: true },
          { label: "Soglie", value: `${detail.soglia_min ?? "—"} / ${detail.soglia_max ?? "—"} °C`, mono: true },
          { label: "Tipo punto", value: detail.tipo || "—" },
          { label: "Stato dispositivo", value: STATI_DISP.find(s => s.v === detail.stato_dispositivo)?.l || "Operativo" },
        ] : []}
        photoUrl={detail?.photo_path ? `${api.defaults.baseURL}/temperature/${detail.id}/photo` : null}
        photoLabel="Foto termometro"
        note={detail?.note}
        onEdit={detail && user?.role !== "operator" ? () => { setEditing(detail); setDetail(null); } : null}
      />
    </div>
  );
}
